import { useStore } from '@vromo/mobx-react/mobx-react';
import { getETAsForJobFromCourierInfo } from '@vromo/models/CourierInfo';
import TrackingLinkStore from '@vromo/tracking-link/TrackingLinkStore';
import Icon from '@vromo/ui/components/FontAwesome';
import { format } from 'date-fns';
import { sumBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { qaLabelTrackingLink } from '../../../../../common-qa-labels/tracking-link';
import colors from '../../../utils/colors';
import styles from '../../orderTracking.style';
import { Job, RouteLeg, Task } from '@vromo/models';

const calculateInternalETA = (job: Job, task?: Task) => {
    if (!job.tasks || !job.route) return 0;
    const lastCompletion =
        job.progress === 0
            ? job?.underway?.created
            : job.tasks.items[(job?.progress || 0) - 1]?.completed?.time;

    return (
        (lastCompletion ?? new Date().valueOf()) +
        sumBy(
            job.route.slice(job.progress, (task?.ix || 0) + 1),
            (it: RouteLeg) => it?.duration || 0
        )
    );
};

const getTimeDiffText = (eta: number) => {
    if (Date.now() > eta) return 'running late';
    const diffInMinutes = Math.ceil((eta - Date.now()) / (1000 * 60));
    if (diffInMinutes < 60) {
        return `in ${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'}`;
    }

    const diffInHours = Math.ceil(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `in ${diffInHours} hour${diffInHours === 1 ? '' : 's'}`;
    }

    const diffInDays = Math.ceil(diffInHours / 24);
    if (diffInDays < 7) {
        return `in ${diffInDays} day${diffInDays === 1 ? '' : 's'}`;
    }
    return 'a while away';
};

const ETADisplay = observer(() => {
    const { t } = useTranslation();
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const job = rootStore.jobs.first()!;
    const [usingCourierInfoETA, setUsingCourierInfoETA] = useState<boolean>(false);
    const [ETA, setETA] = useState<number>(0);

    const courierInfoETA = getETAsForJobFromCourierInfo(job?.courierInfo);

    useEffect(() => {
        if (job.status === 'open') return;

        if (!!courierInfoETA) {
            setUsingCourierInfoETA(true);
            setETA(courierInfoETA?.dropoffEta ?? 0);
        } else if (!ETA && !usingCourierInfoETA) {
            // internal ETA
            setETA(calculateInternalETA(job, job.tasks?.items.at(-1)));
        }
    }, [job, job.progress, ETA, usingCourierInfoETA, job.status, courierInfoETA]);

    useEffect(() => {
        if (job.status === 'open') {
            setUsingCourierInfoETA(false);
            setETA(0);
        }
    }, [job.status]);

    if (job?.cancelled) {
        return (
            <div style={styles.titleRow}>
                <div style={{ ...styles.iconCircle, backgroundColor: colors.errorBackground }}>
                    <Icon icon={'times'} color={colors.errorIcon} />
                </div>
                <div style={styles.title} data-testid={qaLabelTrackingLink.cancelledTitle}>
                    {t('cancelledTitle')}
                </div>
            </div>
        );
    }

    if (job?.finished) {
        return (
            <div style={styles.titleRow}>
                <div style={styles.iconCircle}>
                    <Icon icon={'check'} color={colors.successIcon} />
                </div>
                <div style={styles.title} data-testid={qaLabelTrackingLink.deliveredTitle}>
                    {t('deliveredTitle')}
                </div>
            </div>
        );
    }

    const shouldHideETA =
        job.status === 'open' || (!usingCourierInfoETA && (job?.progress || 0) < 2);
    const subtitleText = shouldHideETA ? t('noETA') : getTimeDiffText(ETA);

    return (
        <>
            <div style={styles.etaTitle} data-testid={qaLabelTrackingLink.etaTitle}>
                {t('etaTitle')}
            </div>
            {!shouldHideETA ? (
                <>
                    <div style={styles.etaTime} data-testid={qaLabelTrackingLink.etaText}>
                        {format(ETA || 0, 'h:mma')}
                        <span
                            style={styles.etaTimeDescription}
                            data-testid={qaLabelTrackingLink.etaSubtitle}>
                            {subtitleText}
                        </span>
                    </div>
                    <div style={styles.etaMessage} data-testid={qaLabelTrackingLink.etaMessage}>
                        <Icon color="orange" icon={'info-circle'} />
                        {t('etaMessage')}
                    </div>
                </>
            ) : (
                <div
                    style={styles.etaTimeDescription}
                    data-testid={qaLabelTrackingLink.etaSubtitle}>
                    {subtitleText}
                </div>
            )}
        </>
    );
});

export default ETADisplay;
